import { RoundStatus } from 'types/types'

type GtagFn = (...args: unknown[]) => void

declare global {
  interface Window {
    gtag?: GtagFn
  }
}

const trackEvent = (name: string, params: Record<string, unknown>) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('event', name, params)
}

export type ShareContentType = 'picks' | 'results'
export type ShareSource =
  | 'score_banner'
  | 'predictions_set_banner'
  | 'round_carousel'
  | 'round_picker'
export type ShareChannel = 'share_now' | 'whatsapp'
export type ShareMethod = 'native_share' | 'fallback'

const getShareContentType = (roundStatus: keyof typeof RoundStatus): ShareContentType =>
  roundStatus === RoundStatus.UPCOMING ? 'picks' : 'results'

// Fired when the Share Predictions/Results CTA is clicked, before the share modal opens.
export const trackShareButtonClick = (params: {
  roundStatus: keyof typeof RoundStatus
  gameId: number
  roundId?: number
  source: ShareSource
}) =>
  trackEvent('share_button_click', {
    content_type: getShareContentType(params.roundStatus),
    game_id: params.gameId,
    round_id: params.roundId,
    source: params.source,
  })

// Fired once a share action from the modal actually completes (native share sheet
// accepted, WhatsApp opened, or the image fallback download triggered).
export const trackShare = (params: {
  channel: ShareChannel
  method: ShareMethod
  roundStatus: keyof typeof RoundStatus
  gameId: number
}) =>
  trackEvent('share', {
    channel: params.channel,
    method: params.method,
    content_type: getShareContentType(params.roundStatus),
    item_id: params.gameId,
  })

// Fired when a share action throws (excludes the user cancelling the native share sheet).
export const trackShareFailed = (params: {
  channel: ShareChannel
  roundStatus: keyof typeof RoundStatus
  gameId: number
  errorMessage: string
}) =>
  trackEvent('share_failed', {
    channel: params.channel,
    content_type: getShareContentType(params.roundStatus),
    item_id: params.gameId,
    error_message: params.errorMessage,
  })

// Fired when a user views the prize draw listing page or an individual draw's detail page.
export const trackPrizeDrawView = (params: {
  viewType: 'list' | 'item'
  contentIds?: (string | number)[]
  contentName?: string
}) =>
  trackEvent(params.viewType === 'list' ? 'view_item_list' : 'view_item', {
    items: params.contentIds?.map((id) => ({ item_id: String(id) })),
    item_list_name: params.viewType === 'list' ? 'prize_draw_list' : undefined,
    item_name: params.contentName,
  })

// Fired once a wallet deposit (top-up) actually completes.
export const trackDeposit = (params: { value: number; currency?: string }) =>
  trackEvent('purchase', { value: params.value, currency: params.currency ?? 'GBP' })
